import dayjs, { Dayjs } from "../config/dayjs";

type TimePeriod = "full" | "halfDay";

/**
 * Count date days based on dates and time periods
 * @param start - Start date
 * @param end - End date
 * @param startPeriod - Start period (full or halfDay)
 * @param endPeriod - End period (full or halfDay)
 * @param includeWeekends - Whether to include weekends (default: false, set true for Weekend OT)
 * @returns Total date days
 */
export const countDateDays = (
  start: Dayjs | null,
  end: Dayjs | null,
  startPeriod: TimePeriod,
  endPeriod: TimePeriod,
  includeWeekends: boolean = false
): number => {
  if (!start || !end) return 0;

  const startDateObj = start.toDate();
  const endDateObj = end.toDate();

  // Normalize to start of day
  startDateObj.setHours(0, 0, 0, 0);
  endDateObj.setHours(0, 0, 0, 0);

  const msPerDay = 1000 * 60 * 60 * 24;
  const dayDiff = Math.round(
    (endDateObj.getTime() - startDateObj.getTime()) / msPerDay
  );

  // Helper to check weekend
  const isWeekend = (date: Date): boolean => {
    const day = date.getDay();
    return day === 0 || day === 6; // Sunday (0), Saturday (6)
  };

  let totalDays = 0;

  if (dayDiff === 0) {
    // Same day
    if (!includeWeekends && isWeekend(startDateObj)) return 0;

    // If both are full day, count as 1
    // If any is half day, count as 0.5
    if (startPeriod === "full" && endPeriod === "full") {
      totalDays = 1;
    } else {
      totalDays = 0.5;
    }
  } else {
    // Multi-day
    for (let i = 0; i <= dayDiff; i++) {
      const current = dayjs(startDateObj).add(i, "day").toDate();

      if (!includeWeekends && isWeekend(current)) continue;

      if (i === 0) {
        // First day
        totalDays += startPeriod === "full" ? 1 : 0.5;
      } else if (i === dayDiff) {
        // Last day
        totalDays += endPeriod === "full" ? 1 : 0.5;
      } else {
        // Full days in between
        totalDays += 1;
      }
    }
  }

  return totalDays;
};
